import { Slot } from "@radix-ui/react-slot"
import { ComponentProps, forwardRef, Fragment, ReactNode } from "react"
import { Icon, IconRef } from "./Icon"
import { cn, tv, VariantProps } from "@utils/tw"
import { Spinner } from "./Spinner"
import { Link } from "eddev/routing"

export type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonStyles> & {
    asChild?: boolean
    leftIcon?: IconRef
    rightIcon?: IconRef
    icon?: IconRef
    href?: string | null
    target?: string
  }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, icon, leftIcon, rightIcon, loading, href, target, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const Wrapper = href ? Link : Fragment
    const wrapperProps = href ? { href, target } : {}
    const styles = buttonStyles({ variant, size, className, loading })
    return (
      <Wrapper {...wrapperProps}>
        <Comp className={cn(styles)} ref={ref} {...props} type={props.type ? props.type : "button"}>
          {leftIcon ? <Icon data-part="icon" name={leftIcon} /> : null}
          {icon ? <Icon data-part="icon" name={icon} /> : null}
          {props.children ? <span data-part="label">{props.children}</span> : null}
          {loading ? <Spinner data-part="spinner" /> : null}
          {rightIcon ? <Icon data-part="icon" name={rightIcon} /> : null}
        </Comp>
      </Wrapper>
    )
  },
)
Button.displayName = "Button"

export const buttonStyles = tv({
  base: [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap relative outline-none",
    "transition-colors",
    "focus-visible:ring-input disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    "[&_.icon:first-child]:-ml-2",
    "[&_.icon:last-child]:-mr-2",
    "[&_.icon]:size-[1.4em] part-[spinner]:size-[1.3em]",
    "part-[label]:mt-[0.1em]",
    "part-[spinner]:absolute part-[spinner]:top-1/2 part-[spinner]:left-1/2 part-[spinner]:transform part-[spinner]:-translate-x-1/2 part-[spinner]:-translate-y-1/2",
  ],
  variants: {
    variant: {
      default: "bg-primary text-primary-foreground hover:bg-primary/80",
      destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      outline:
        "border border-primary bg-bg text-secondary-foreground hover:border-accent hover:bg-accent hover:text-accent-foreground",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/60 data-[state=open]:bg-secondary/60",
      ghost:
        "hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-secondary data-[state=open]:text-secondary-foreground",
      link: "text-primary underline-offset-4 hover:underline",
    },
    size: {
      default: "h-control type-label-m px-4",
      sm: "h-control-sm px-3 type-label-s-light",
      lg: "h-control-lg type-label-l uppercase px-4",
      icon: "h-control w-control type-label-m",
    },
    loading: {
      true: ["pointer-events-none opacity-90", "[&_>_*]:opacity-0 part-[spinner]:opacity-100"],
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})
